import React, { FC, memo, useMemo } from 'react'

import { fromUnixTime } from 'date-fns'

import {
  analyticsVideoTypes,
  eventAction,
  eventObject,
  videoPlayerNames,
} from '@/constants/analytics'
import { focusIds } from '@/constants/focus'
import { channelSliderImgWidth } from '@/constants/tiles'
import { useActiveBroadcast } from '@/hooks/useActiveBroadcast'
import { dateTranslation } from '@/i18n/dateTranslation'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { ChannelItemElemProps } from '@/types/componentsPropsTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'
import { findNearestByImgWidth, getTimeFormat } from '@/utils/utils'

import {
  ChannelItemElem,
  ChannelLogo,
  ChannelEvent,
  Description,
  Time,
  ElementItem,
  Name,
} from './styles'

const getTranslatedDateRange = (
  start: number | undefined,
  end: number | undefined,
) => {
  if (!start || !end) {
    return null
  }

  const timeFormat = getTimeFormat()
  const dateStart = dateTranslation(fromUnixTime(start), timeFormat)
  const dateEnd = dateTranslation(fromUnixTime(end), timeFormat)

  return `${dateStart} - ${dateEnd}`
}

const ChannelItemRaw: FC<ChannelItemElemProps> = ({
  data,
  id,
  broadcastId,
}) => {
  const { t } = useCommonTranslation()

  const { activeChannel } = useActiveBroadcast(broadcastId)
  const setActiveBroadcastChannel = useStore(
    (state) => state.common.setActiveBroadcastChannel,
  )
  const channelEvent = useStore((state) => state.common.channelEvents[id])
  const currentEvent = channelEvent?.isFutureChannelEvent ? null : channelEvent

  const eventName = currentEvent?.name || data.name
  const start = currentEvent?.startTime
  const end = currentEvent?.endTime
  const eventTime = getTranslatedDateRange(start, end) || t('nowLive')

  const eventCategoryName = currentEvent?.categoryName
    ? ` | ${currentEvent.categoryName}`
    : ''

  const imgUrl = useMemo(
    () =>
      findNearestByImgWidth(data?.image?.sources, channelSliderImgWidth, {
        prioritizeSvg: true,
      })?.url,
    [data?.id],
  )

  const onSelected = () => {
    data && setActiveBroadcastChannel(broadcastId, data)
    data &&
      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.START_CLICK,
        video: {
          id: currentEvent?.id || data.id,
          name: currentEvent?.name || data.name || data.id,
          playerName: videoPlayerNames.HLS,
          type: analyticsVideoTypes.STREAM,
          link:
            currentEvent?.video?.sources?.[0]?.url ||
            data.video?.sources?.[0]?.url,
          partnerIds: currentEvent?.partnerIds || data.partnerIds,
        },
      })
  }

  return (
    <ElementItem name={`${focusIds.CHANNEL_ITEM}${broadcastId}${id}`}>
      <ChannelItemElem
        focusId={`${focusIds.CHANNEL_ITEM}${broadcastId}${id}`}
        onSelected={onSelected}
        className={id === activeChannel?.id ? 'isChannelActive' : ''}
      >
        <ChannelLogo>
          <img src={imgUrl} alt="" />
        </ChannelLogo>
        <ChannelEvent>
          <Description>
            <Name>{eventName}</Name>
            <Time>{eventTime + eventCategoryName}</Time>
          </Description>
        </ChannelEvent>
      </ChannelItemElem>
    </ElementItem>
  )
}

export const ChannelItem = memo(ChannelItemRaw)
